import GeoJSON, { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import _, { join, trim } from "lodash";
import { Coordinate, distance, equals } from "ol/coordinate";
import BaseEvent from "ol/events/Event";
import { Extent, createEmpty, scaleFromCenter, createOrUpdateFromCoordinates, getBottomLeft, getTopRight, extend, intersects, containsExtent } from "ol/extent";
import { WriteOptions } from "ol/format/Feature";
import LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import { Interaction } from "ol/interaction";
import { CheckSubscriptionBySessionIdCommand } from "./CheckSubscriptionBySessionIdCommand";
import CloudGpxInfo from "./CloudGpxInfo";
import CloudNameChecker from "./CloudNameChecker";
import CloudSaveHelper from "./CloudSaveHelper";
import CloudSaveModel from "./CloudSaveModel";
import CloudSaveService from "./CloudSaveService";
import { ColourValues, IconColours } from "./Colours";
import { CommonEvents } from "./CommonEvents";
import ConfirmAccountCommand from "./ConfirmAccountCommand";
import { freeUserEditSharedMessage, freeUserDownloadMessage, freeUserShareMessage, freeUserTrackLimitMessage } from "./Constants";
import { ContextMenuCommandType } from "./ContextMenu";
import ContextMenuEvent, { ContextMenuEventType } from "./ContextMenuEvent";
import CurrentPosition from "./CurrentPosition";
import CustomInteractionLifecycle from "./CustomInteractionLifecycle";
import CustomInteractionLifecycleTrack from "./CustomInteractionLifecycleTrack";
import CustomVectorLayer from "./CustomVectorLayer";
import DeleteTrackData from "./DeleteTrackData";
import DeleteTrackEvent from "./DeleteTrackEvent";
import DeleteWaypointData from "./DeleteWaypointData";
import DrawRoute, { DrawRouteEvents } from "./drawroute";
import DrawWaypoint from "./drawwaypoint";
import EditWaypoint, { WaypointEvents, WaypointSelectedEvent } from "./editwaypoint";
import ErrorHelper from "./ErrorHelper";
import { cleanFileName } from "./FileNameUtility";
import GpxDownloadService from "./GpxDownloadService";
import GpxGeoJson from "./GpxGeoJson";
import GpxGeoJsonTrack from "./GpxGeoJsonTrack";
import GpxGeoJsonWaypoint from "./GpxGeoJsonWaypoint";
import InfoMessageEvent from "./InfoMessageEvent";
import Journey from "./Journey";
import LocationService from "./LocationService";
import LogoutService from "./LogoutService";
import PointGeometry from "./PointGeometry";
import PreUndoData from "./PreUndoData";
import PreUndoEvent from "./PreUndoEvent";
import Price from "./Price";
import Product from "./Product";
import PublicShareInfo from "./PublicShareInfo";
import RegistrationService from "./RegistrationService";
import ReinstateTrackData from "./ReinstateTrackData";
import ReinstateWorkspaceData from "./ReinstateWorkspaceData";
import RendererKey from "./RendererKey";
import { RendererMetadata } from "./RendererMetadata";
import Reroute, { RerouteConstructor, RerouteBase, RerouteErrorEvent, RerouteEvents, RerouteWarningEvent } from "./reroute";
import RestoreTrackData from "./RestoreTrackData";
import RestoreWaypointData from "./RestoreWaypointData";
import { RouteDelegateImp } from "./RouteDelegateImp";
import router from "./router";
import SelectTrack, { SelectTrackEvent } from "./selecttrack";
import SetCurrentTrackData from "./SetCurrentTrackData";
import ShowMenu from "./showmenu";
import SplitTrack, { NewTrackEvent, SplitTrackEvents } from "./splittrack";
import store from "./store";
import StripeService from "./StripeService";
import { Styles } from "./Styles";
import { TrackEntity } from "./TrackEntity";
import TrackVectorLayer from "./TrackVectorLayer";
import { TrackVectorLayerType } from "./TrackVectorLayerType";
import Undoable from "./Undoable";
import UndoCommand from "./UndoCommand";
import UndoCommandParameterized from "./UndoCommandParameterized";
import { UserMessageCommand } from "./UserMessageCommand";
import WaypointFeature, { WPT_UNTITLED } from "./WaypointFeature";
import { WaypointIcon } from "./WaypointIcon";
import WaypointLayer from "./WaypointLayer";
import WaypointSerialization from "./WaypointSerialization";
import AddNewTrackOptions from './AddNewTrackOptions';
import IsWaypointSelectedDelegate from './IsWaypointSelectedDelegate';
import SetDefaultCursorDelegate from './SetDefaultCursorDelegate';
import HomeComputed from './HomeComputed';
import { Feature, MapBrowserEvent } from 'ol';
import StorageHelper from './StorageHelper';
import ElevationChartHelper, { ElevationChartDataInfo } from './ElevationChartHelper';
import { latLngProjection } from './Renderer';
import DistanceUnits, { DistanceUnit, feetPerMetre } from './DistanceUnits';
import { ActiveElement, Chart, ChartConfiguration, ChartDataset, ChartEvent, ChartTypeRegistry, InteractionItem, ScatterDataPoint } from 'chart.js';
import ElevationChartHoverIndicator from './ElevationChartHoverIndicator';
import VectorLayer from 'ol/layer/Vector';
import DistanceHelper from './DistanceHelper';
import PaneSpec from './PaneSpec';
import { AnyObject } from 'chart.js/types/basic';
import RerouteIndividual from './RerouteIndividual';
import findNearestDataPoint from './BinaryChop';
import ElevDataPoint from './ElevDataPoint';
import RestoreCachedGpxCommand from './RestoreCachedGpxCommand';
import LoginService from './LoginService';
import JoinHelper from './JoinHelper';
import HomeEvents from './HomeEvents';
import { AllTracksInOneGpx, CurrentTrack, DefaultDownloadOption } from './HomeData';
import { Circle } from 'ol/style';
import LineStringFeature from './LineStringFeature';
import { CloudGpxType } from './CloudGpxType';
import { NameAZ, NameZA, NewOld, OldNew } from './CloudGpxSortDirection';
import HoverDistanceInteraction from './HoverDistanceInteraction';
import { Loader } from '@googlemaps/js-api-loader';
import GoogleLoginCommand from './GoogleLoginCommand';
import RestoreCachedGpxAlreadyRetrievedCommand from './RestoreCachedGpxAlreadyRetrievedCommand';

export default class HomeMethods extends HomeComputed implements IsWaypointSelectedDelegate, SetDefaultCursorDelegate, HomeEvents {
    mergeTrackSegmentsCurrentTrack() {
        const currentTrack = this.getCurrentTrack();
        if(currentTrack) {
            this.mergeSegments([currentTrack])
        }
    }
    mergeTrackSegmentsAllTracks() {
        this.mergeSegments(this.tracks);
    }
    mergeSegments(tracks: TrackEntity[]) {
        if(tracks.length > 0) {
            tracks.forEach(t => t.saveFeaturesToLngLats(this.currentRenderer));
            const restoreData = this.tracks.map(t => new RestoreTrackData(t.value, t.dotLngLats, t.trackLngLatFeatures));
            this.undoCommands.push(new Undoable(restoreData.map(r => this.getCommandForUndoData(r)), 'merge segments (all)'))
            tracks.forEach(t => {
                t.flatten();
                t.loadFeaturesFromLngLats(this.currentRenderer);
            })
        }
    }
    saveToFileUsingPublicForm(): boolean {
        return !store.getters.isSubscribed && this.loadedTrackGpxUrl !== null
    }
    downloadGpxUrl() {
        return this.saveToFileUsingPublicForm() ? GpxDownloadService.downloadGpxPublicFormUrl() : GpxDownloadService.downloadGpxFormUrl()
    }
    setCurrentTrackType(trackType: string) {
        if(!this.isFreeUserTryingToEditSharedTrack()) {
            this.currentTrackType = trackType
            this.setCurrentTrackColour (this.currentTrackColour)
        }
    }
    isFreeUserTryingToEditSharedTrack() {
        if(this.loadedTrackGpxUrl !== null && !store.getters.isSubscribed) {
            this.showSubscribeOnlyDialog(freeUserEditSharedMessage, this.subscriberOnlyDrawInNewWorkspace)
            return true
        }
        return false
    }
    colour(colourName: string) {
        return ColourValues[colourName]
    }
    colourOrDefault(colour: string|null) {
        if (colour) return colour;
        return (this.currentTrackType == "route") ? Styles.DefaultRouteColour : Styles.DefaultTrackColour
    }
    iconColour(colour: string) {
        return IconColours[colour]
    }
    setWaypointIcon(icon: string) {
        if(this.selectedWaypoint) {
            this.selectedWaypoint.setIcon(icon)
        }

        this.showWaypointIconPicker = false
    }
    setWaypointColour(colour: string) {
        if(this.selectedWaypoint) {
            this.selectedWaypoint.setColour(colour);
        }
        this.showWaypointColourPicker = false
    }
    invokeCurrentTrackColourPicker() {
        if(!this.isFreeUserTryingToEditSharedTrack()) {
            this.showCurrentTrackColourPicker = true
        }
    }
    setCurrentTrackColour(colourName: string) {
        this.currentTrackColour = colourName
        this.showCurrentTrackColourPicker = false
    }
    closeSidebarIfMobile() {
        const navDrawer = document.getElementById("navigationDrawer")
        if(navDrawer && navDrawer.classList.contains('v-navigation-drawer--is-mobile')) {
            this.sidebar = false
        }
    }
    splitTrack() {
        this.operation = this.operations.findIndex(o => o.name === 'split')
        this.closeSidebarIfMobile()
    }
    reverseTrack() {
        const currentTrack = this.getCurrentTrack()
        if(currentTrack) {
            const undoData = currentTrack.reverse(this.currentRenderer)
            this.undoCommands.push(new Undoable([this.getCommandForUndoData(undoData)], 'reverse track'))
            this.currentOperations.forEach(op => {
                if(op && this.implementsCustomInteractionLifecycleTrack(op)) {
                    op.trackChanged()
                }                    
            })
        }
    }
    cancelSplitTrack() {
        this.operation = this.operations.findIndex(o => o.name === 'move')
    }
    startPlaceSearch() {
        this.showPlaceSearch = true
        this.$nextTick(() => {
            try {
                const extent = this.currentRenderer.Extent() // this has been known to throw an exception for some unknown reason - not sure why, but just catch it and see if anybody reports it, then we might learn more about the situation
                const placeSearchTextBox = document.getElementById('placesearch') as HTMLInputElement
                const autoCompleteOptions: google.maps.places.AutocompleteOptions = {
                    bounds: {
                        west: extent[0],
                        south: extent[1],
                        east: extent[2],
                        north: extent[3]
                    }
                }
                const googleMapsLoader = new Loader({
                    apiKey: "AIzaSyDhqSyw5njFyVQ1NkhbSC9iKHAb7N1sl7M"
                })
                googleMapsLoader.load().then(async () => {
                    const { AutocompleteService } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
                    this.placeSearchAutoComplete = new google.maps.places.Autocomplete(placeSearchTextBox, autoCompleteOptions)
                    this.placeSearchAutoComplete.addListener('place_changed', this.placeSearchPlaceChanged.bind(this))
                })
        
        
                // this.placeSearchAutoComplete = new google.maps.places.Autocomplete(placeSearchTextBox, autoCompleteOptions)
                // 
                placeSearchTextBox.focus()
            } catch(error) {
                this.showError('Could not initialize place search - if you could please report this with a screenshot to admin@gpxplanner.app it would be very much appreciated. Thank you.');
                this.showPlaceSearch = false;
            }
        })
    }
    hidePlaceSearch() {
        this.placeSearchAutoComplete = null
        this.showPlaceSearch = false
    }
    placeSearchPlaceChanged() {
        if(this.placeSearchAutoComplete) {
            const place = this.placeSearchAutoComplete.getPlace()
            if(place.geometry?.viewport) {
                const viewportSW = place.geometry.viewport.getSouthWest()
                const viewportNE = place.geometry.viewport.getNorthEast()
                const latLngExtent: Extent = [viewportSW.lng(), viewportSW.lat(), viewportNE.lng(), viewportNE.lat()]
                this.currentRenderer.GoToLatLngExtent(latLngExtent)
                this.hidePlaceSearch()
            }
            if(place.geometry?.location && place.name) {
                const coord = this.currentRenderer.TransformCoordFromLatLng([place.geometry.location.lng(), place.geometry.location.lat()])
                this.addWaypoint(coord).props.name = place.name
            }
        }
    }
    async importGpx(gpxInfo: CloudGpxInfo) {
        try {
            const gpx = await CloudSaveHelper.CloudSaveGetP(gpxInfo.public, gpxInfo.url)
            let overallExtent: Extent = createEmpty()
            const extentCallback: ((ext: Extent) => void) = ext => {
                overallExtent = extend(overallExtent, ext)
            }
            this.addTracksFromGpxGeoJson(gpx.data.gpx, extentCallback)
            this.addWaypointsFromGpxGeoJson(gpx.data.gpx, extentCallback)
            scaleFromCenter(overallExtent, 1.1) // so features don't appear right on the edge
            this.currentRenderer.GoToLatLngExtent(overallExtent)
            this.operation = 0
            this.cloudsidebar = false
            this.showInfo(`Successfully imported ${gpxInfo.name}`)
        } catch(error) {
            console.error(error)
            ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) || this.showError('Error importing track')
        }
    }
    addRendererIfKeyPresent(allKeys: RendererKey[], renderers: RendererMetadata<any>[], service: string, creator: ((key: string) => RendererMetadata<any>)) {
        const rendererKey = allKeys.find(k => k.service === service)
        if(rendererKey) {
            const rendererKeySample = _.sample(rendererKey.keys)
            if(rendererKeySample) {
                renderers.push(creator(rendererKeySample))
            } else {
                console.warn('No renderer keys for ' + service)
            }
        } else {
            console.warn('No renderer key for ' + service)
        }
    }
    async getDefaultExtent() {
        try {
            const extentResponse = await LocationService.DefaultExtent()
            return createOrUpdateFromCoordinates([extentResponse.data.bottomLeft, extentResponse.data.topRight])
        } catch(error) {
            console.error(error);
            return createOrUpdateFromCoordinates([[-4, 49], [2, 61]])
        }
    }
    
    setupRendererOnInitOrChange(loadCurrentPosition = true) {
        if(loadCurrentPosition) {
            this.loadCurrentPosition()
        }
        this.currentRenderer.map.on('moveend', (e: any) => {
            const position = this.getCurrentPosition()
            this.saveCurrentPosition(position)
            this.filterRenderers(position)
            this.redrawElevationChartWhenMapChanges()
        })
        this.currentRenderer.contextMenuControl.contextMenu.$on(ContextMenuEventType.Command, this.onContextMenuCommand.bind(this))
        this.addPermanentInteractions()
        this.currentRenderer.setScaleLineUnit(this.distanceUnit)
    }

    // panning or zooming the map will only require the elev chart to re-draw if the 
    // current track now lies (or previously lay) partly outside the map extent
    redrawElevationChartWhenMapChanges() {
        const track = this.getCurrentTrack()
        if (this.elevationChart !== null && track) {
            const nowPartlyVisible = !containsExtent(this.currentRenderer.GetExtent(), track.GetExtent())
            if (this.wasPartlyVisible || nowPartlyVisible) this.debouncedshowOrRedrawElevationChart()
            this.wasPartlyVisible = nowPartlyVisible
        }
    }
    private debouncedshowOrRedrawElevationChart = _.debounce(this.showOrRedrawElevationChart, 500) 
    private wasPartlyVisible = true // whether the current track previously lay partly or wholly outside the map extent

    onContextMenuCommand(commandType: ContextMenuCommandType) {
        this.currentRenderer.contextMenuControl.hide()
        if(commandType === ContextMenuCommandType.CopyLatLngToClipboard) {
            this.onVerifyingContextMenuCoords(this.currentRenderer.ContextMenuLatLngCoordinates(), this.copyLatLngToClipboard.bind(this))
        } else if(commandType === ContextMenuCommandType.OpenInGoogleMaps) {
            this.onVerifyingContextMenuCoords(this.currentRenderer.ContextMenuLatLngCoordinates(), this.openInGoogleMaps.bind(this))
        } else if(commandType === ContextMenuCommandType.AddWaypoint) {
            this.onVerifyingContextMenuCoords(this.currentRenderer.contextMenuControl.coordinate, this.addWaypoint.bind(this))
        }
    }
    onVerifyingContextMenuCoords(coords: Coordinate|undefined, action: (coords: Coordinate) => void) {
        if(coords === undefined) {
            console.error('Context menu did not have coordinates')
            this.showError('The context menu did not have coordinates. Please report this to admin@gpxplanner.app')
        } else {
            action(coords)
        }
    }
    async copyLatLngToClipboard(latLngCoords: Coordinate) {
        await navigator.clipboard.writeText(`${latLngCoords[1].toFixed(5)},${latLngCoords[0].toFixed(5)}`) // lng is first (x,y)
        this.showInfo('Copied lat/lng to clipboard')
    }
    async openInGoogleMaps(latLngCoords: Coordinate) {
        const url = `https://google.com/maps/place/${latLngCoords[1].toFixed(5)},${latLngCoords[0].toFixed(5)}`
        window.open(url)
    }
    addWaypoint(coords: Coordinate) { //not a lat/lng coord
        const newIndex = this.waypoints.dotsFeatureSource.getFeatures().length
        const point = new Point(coords)
        const waypointFeature = new WaypointFeature(point, this.waypoints, newIndex)
        this.undoCommands.push(new Undoable([this.getCommandForUndoData(new DeleteWaypointData(waypointFeature.value))], 'add waypoint'))
        this.waypoints.dotsFeatureSource.addFeature(waypointFeature)
        return waypointFeature
    }
    filterRenderers(position: CurrentPosition) {
        this.validRenderers = this.renderers.filter(r => r.isValidForExtent(position.extent) || r.value === this.renderer) // don't remove the selected one if panned out
    }
    showSubscribeOnlyDialog(text: string, extraMessage?: string) {
        this.subscriberOnlyExtraMessage = extraMessage ?? null
        this.subscriberOnlyDialogText = text
        this.showSubscriberOnlyDialog = true
    }
    dismissSubscriberOnlyDialog(showSubscriptionOptions: boolean) {
        this.showSubscriberOnlyDialog = false
        this.subscriberOnlyDialogText = null
        if(showSubscriptionOptions) {
            store.commit('showingPricingSidebar', true);
        }
    }
    noTrackContent() {
        // only one and no features - no point deleting, because that's the start pos
        if(( this.tracks.length === 0) || // there probably shouldn't actually ever be zero, but that's not an implementation quirk that is not a concern for this logic
            this.tracks.length === 1 && this.tracks[0].isDegenerate()) {
            return true;
        }
        else return false;
    }

    noWaypoints() {
        return this.waypoints.isDegenerate()
    }
    noContent() {
        return this.noTrackContent() && this.noWaypoints()
    }

    currentTrackHasNoSegments() {
        const track = this.getCurrentTrack()
        return !(track && track.hasSegments)
    }
    static removeAltitudesIfAllHaventGotThem(segments: GeoJSONFeatureCollection) {
        // this is necessary because instead of sending [lng, lat], it sends [lng, lat, null] and LngLatAlt on the server (which is a library class)
        // can't cope with that. A potential fix if this is ever an issue would be to create a custom version of LngLatAlt to deserialize to
        // that can handle nulls
        const haveAnyGotInvalidAltitudes = _.some(segments.features, f => {
            if(f.geometry.type === 'LineString') {
                return _.some(f.geometry.coordinates, c => c.length > 2 && !(c[2]))
            } else {
                return false
            }
        })
        if(haveAnyGotInvalidAltitudes) {
            segments.features.forEach(f => {
                if(f.geometry.type === 'LineString') {
                    f.geometry.coordinates.forEach(c => c.splice(2))
                }
            })
        }
    }
    getGpx(): GpxGeoJson {
        const g = new GeoJSON()
        const gpxTracks: GpxGeoJsonTrack[] = []
        const waypoints: GpxGeoJsonWaypoint[] = []
        const writeOptions: WriteOptions = {
            decimals: 5,
            featureProjection: this.currentRenderer.projection,
            dataProjection: latLngProjection
        }
        this.tracks.forEach(track => {
            const segments = g.writeFeaturesObject(track.getSegments(), writeOptions)
            HomeMethods.removeAltitudesIfAllHaventGotThem(segments)
            gpxTracks.push({
                name: track.name,
                type: track.type,
                colour: track.colour,
                segments: segments
            })
        })
        
        this.waypoints.dotsFeatureSource.getFeatures().forEach(wp => {
            waypoints.push({
                name: wp.props.name,
                icon: wp.iconOrBlank(),
                colour: wp.colourOrBlank(),
                proximityAlarm: wp.props.proximityAlarm,
                waypoint: g.writeFeatureObject(wp, writeOptions)
            })
        })

        return {
            tracks: gpxTracks,
            waypoints: waypoints
        }
    }
    addTrack(track: TrackEntity) {
        this.tracks.push(track)
        track.loadFeaturesFromLngLats(this.currentRenderer)
        this.addTrackLayersToMap(track)
    }
    addTracksFromGpxGeoJson(gpxGeoJson: GpxGeoJson, trackExtentCallback: ((ext: Extent) => void) | undefined = undefined) {
        const g = new GeoJSON()
        gpxGeoJson.tracks.forEach(t => {
            // map the track to an entity (need its dots, routing lines, etc)
            const track = new TrackEntity(this.currentRenderer)
            track.name = t.name ?? (`Untitled${TrackEntity.nextTrackId++}`)
            track.type = t.type
            track.colour = t.colour
            let lastCoordinate = null as Coordinate|null
            g.readFeatures(t.segments).forEach(f => {
                const geom = f.getGeometry()
                const feature = f as LineStringFeature
                
                if(geom instanceof LineString && geom.getCoordinates().length >= 2) {
                    if(trackExtentCallback) {
                        trackExtentCallback(geom.getExtent())
                    }
                    track.trackLngLatFeatures.push(feature)
                    track.dotLngLats.push(new Point(geom.getFirstCoordinate()));
                    lastCoordinate = geom.getLastCoordinate()
                }
            })
            if(lastCoordinate !== null) {
                track.dotLngLats.push(new Point(lastCoordinate))
            }

            if(track.trackLngLats.length > 0) {
                this.addTrack(track)
                // is the old currentTrack degenerate?
                
                const oldCurrentTrackIndex = this.tracks.findIndex(t1 => t1.value === this.currentTrack)
                if(oldCurrentTrackIndex >= 0 && this.tracks[oldCurrentTrackIndex].isDegenerate()) {
                    this.removeTrackInternal(oldCurrentTrackIndex)
                }

                this.currentTrack = track.value
            }
        })
    }
    addWaypointsFromGpxGeoJson(gpxGeoJson: GpxGeoJson, waypointExtentCallback: ((ext: Extent) => void) | undefined = undefined) {
        gpxGeoJson.waypoints.forEach(wp => {
            const geom = wp.waypoint.geometry as PointGeometry
            const latLng = new Point(geom.coordinates)
            if(waypointExtentCallback) {
                waypointExtentCallback(latLng.getExtent())
            }
            const point = this.currentRenderer.TransformPointFromLatLng(latLng)
            const newIndex = this.waypoints.dotsFeatureSource.getFeatures().length
            const waypointFeature = new WaypointFeature(point, this.waypoints, newIndex)
            waypointFeature.setAttrs(wp.name, wp.icon, wp.colour, wp.proximityAlarm)
            this.waypoints.dotsFeatureSource.addFeature(waypointFeature)
        })
    }
    inferSuggestedFileName() {
        // infer a suggested filename from the track name; we'll add ".gpx" before saving if it needs it
        if(this.loadedTrackGpxUrl) this.saveFileName = cleanFileName(this.loadedTrackGpxUrl).trim() 
        else this.saveFileName = cleanFileName(this.tracks[0].name).trim()         
    }
    showFileSave() {
        if(!(store.getters.isSubscribed || this.loadedTrackGpxUrl !== null)) {
            this.showSubscribeOnlyDialog(freeUserDownloadMessage)
        } else {
            this.inferSuggestedFileName();

            this.downloadOption = DefaultDownloadOption
            this.trackPointLimitChecker.reset()
            this.gpxFileNameChecker.reset()
            this.showSaveToFileDialog = true
            this.gpxAsString = this.noContent() ? '' : JSON.stringify(this.getGpx())
            setTimeout (() =>  { // select the text in the field, ready for overtype
                const textField = document.getElementById('saveToFileName') as HTMLInputElement
                if(textField) {
                    textField.select()
                }
            })
        }
    }
    onSaveToFileSubmit() {
        this.showSaveToFileDialog = false
    }
    showShare() {
        if(!store.getters.isSubscribed) {
            this.showSubscribeOnlyDialog(freeUserShareMessage)
        } else {
            this.cloudNameChecker = new CloudNameChecker()
            this.cloudNameChecker.name = cleanFileName(this.tracks[0].name).trim()
            this.showShareToCloudDialog = true
            setTimeout(() => {
                const textField = document.getElementById('shareToCloudName') as HTMLInputElement
                if(textField) {
                    textField.select()
                }
            })
        }
    }
    async resave() {
        if(this.loadedTrackGpxUrl && this.loadedTrackOwnedByCurrentUser === true && this.loadedTrackIsPublic !== null) {
            await this.doCloudSave(this.loadedTrackGpxUrl, this.loadedTrackIsPublic, true)
        }
    }
    async doShare() {
        this.cloudNameChecker.hasTriedSave = true
        if(!this.cloudNameChecker.notValid) {
            this.cloudNameChecker.saving = true
            try
            {
                await this.doCloudSave(this.cloudNameChecker.name, this.cloudNameChecker.isPublic, this.cloudNameChecker.overwriting)
                this.showShareToCloudDialog = false
            } catch(error) {
                console.error(error)
                const errorData = error as any
                if(errorData && errorData.response && errorData.response.status === 409) {
                    // tried to save without passing overwrite flag.
                    this.cloudNameChecker.overwriting = true
                } else {
                    ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) || this.showError('An error has occurred')
                }
            } finally {
                this.cloudNameChecker.saving = false
            }
        }
    }
    async doCloudSave(name: string, isPublic: boolean, overwrite: boolean) {
        const gpx = this.getGpx()
        const extent = this.currentRenderer.GetLatLngExtent()
        const model: CloudSaveModel = {
            center: this.currentRenderer.TransformCoordToLatLng(this.currentRenderer.map.getView().getCenter()!),
            gpx: gpx,
            name: name,
            renderer: this.renderer,
            bottomLeft: getBottomLeft(extent),
            topRight: getTopRight(extent),
            overwrite: overwrite
        }
        let savedUrl: string
        let savedGpxInfo: CloudGpxInfo
        this.dialog = '' // get rid of the share to cloud dialog 
        this.progressMessage = "Saving to cloud... please wait"
        try
        {
            if(isPublic) {
                const saveResponse = await CloudSaveService.CloudSavePublic(model)
                savedGpxInfo = saveResponse.data
                this.publicShareInfo = this.getPublicShareInfo(saveResponse.data)            
                savedUrl = this.gpxLink(saveResponse.data)
                store.commit('savePublicGpx', saveResponse.data)
                setTimeout(() => { // this is necessary because without it, Vue fails to clear the overlay from the previous dialog (share)
                    this.showPublicShareDialog = true
                });
                
            } else {
                const saveResponse = await CloudSaveService.CloudSavePrivate(model)
                savedGpxInfo = saveResponse.data
                savedUrl = this.gpxLink(saveResponse.data)
                this.showInfo(`Successfully saved ${name} to cloud`)
                store.commit('savePrivateGpx', saveResponse.data)
            }
        }
        catch(error) {
            ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) || this.showError('An error occurred when saving.')
            throw error
        } finally {
            this.progressMessage = null
        }

        if(this.$route.fullPath !== savedUrl) {
            await router.push({path: savedUrl})
        }

        this.loadedTrackGpxUrl = savedGpxInfo.url
        this.loadedTrackIsPublic = savedGpxInfo.public
        this.loadedTrackOwnedByCurrentUser = true
    }
    async deleteTrack() {
        if(!this.isFreeUserTryingToEditSharedTrack()) {
            this.trackToDelete = this.currentTrackName
        }
    }
    dismissDeleteTrackDialog(choice: boolean) {
        this.trackToDelete = null
        if(choice) {
            this.doDeleteCurrentTrack()
        }
    }
    doDeleteCurrentTrack() {
        const trackIndex = this.tracks.findIndex(t => t.value === this.currentTrack)
        if(trackIndex >= 0) {
            this.doDeleteTrackOfIndex(trackIndex)
        }
    }
    doDeleteTrackOfIndex(trackIndex: number, addUndoCommand = true) {
        this.removeTrackInternal(trackIndex, addUndoCommand)
        if(this.tracks.length === 0) {
            this.addNewTrack()
            return false
        } else {
            const newTrackIndex = Math.min(trackIndex, this.tracks.length - 1)
            this.currentTrack = this.tracks[newTrackIndex].value
            return true
        }
    }
    removeTrackInternal(trackIndex: number, addUndoCommand = true) {
        const track = this.tracks[trackIndex]
        if(addUndoCommand) {
            this.undoCommands.push(new Undoable([this.getCommandForUndoData(track.getReinstateData(this.currentRenderer))], 'delete track'))
        }
        track.layers.forEach(l => {
            this.currentRenderer.map.removeLayer(l)
        })
        this.tracks.splice(trackIndex, 1)
    }
    loadCurrentPosition() {
        const currentPositionString = StorageHelper.getItem("CurrentPosition")
        if(currentPositionString) {
            const currentPosition: CurrentPosition = JSON.parse(currentPositionString)
            this.setCurrentPosition(currentPosition)
       }
    }
    setCurrentPosition(currentPosition: CurrentPosition) {
        if(currentPosition.center && currentPosition.extent) {
            this.currentRenderer.map.getView().setCenter(this.currentRenderer.TransformCoordFromLatLng(currentPosition.center))
            this.currentRenderer.SetResolutionForLatLngExtent(currentPosition.extent)
        }
    }
    getCurrentPosition(): CurrentPosition {
        return {
            center: this.currentRenderer.TransformCoordToLatLng(this.currentRenderer.map.getView().getCenter()!),
            extent: this.currentRenderer.GetLatLngExtent()
        }
    }
    saveCurrentPosition(currentPosition: CurrentPosition) {
        StorageHelper.setItem("CurrentPosition", JSON.stringify(currentPosition))
        return currentPosition
    }
    get currentTrackHash() {
        const track = this.getCurrentTrack()
        return track ? 
            track.value + ':' + track.trackHash
            : ''
    }
    async handleUserMessageCommand(message: UserMessageCommand) {
        this.snackbar = true
        this.snackbarText = message.msg
        this.snackbarColor = message.color
        
        if(message.showLogin) {
            this.dialog = 'login'
        }
    }
    async removeSubscriberInfo(toRemove: boolean) {
        this.$store.commit('dismissSubscriptionInvalid');
        this.dialog = '';
        if(toRemove) {
            await LoginService.removeSubscriptionInfo();
            this.showInfo("Subscription info successfully removed.")
        }
        
    }
    async handleCheckSubscriptionBySessionIdCommand(command: CheckSubscriptionBySessionIdCommand) {
        try {
            const sub = await StripeService.getSubBySessionId(command.sessionId)
            const expiryDate = new Date(sub.data.subscriptionExpiry)
            store.commit('setSubscriptionExpiry', expiryDate)
            let message
            if(sub.data.subscription) {
                if(sub.data.freeTrial) {
                    message = 'Thank you - your free trial has started and you can now access all features.'
                } else {
                    message = 'Thank you - your subscription has started and you can now access all features.'
                }
            } else {
                message = 'Thank you - your payment has been received and you can now access all features.'
            }
            this.showInfo(message, 'green')
        } catch(error) {
            ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) ||
                this.showError('Please try logging out and logging back in to refresh your subscription.')
        }
    }
    async handleConfirmAccountCommand(command: ConfirmAccountCommand) {
        try {
            await RegistrationService.confirmAccount(command.code)
            this.showInfo('Thanks - you have successfully confirmed your account. You can now log in.')
            this.dialog = 'login'
        } catch(error) {
            ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) ||
                this.showError('Could not confirm account. The link may have expired. Please try registering again.')
        }
    }
    clickNavBar() {
        if (!this.dialog) {
            this.sidebar = !this.sidebar
        }
    }
    showCloudGpxs() {
        if(!this.dialog) {
            this.cloudsidebar = true
        }
    }
    showError(message: string) {
        this.snackbarText = message;
        this.snackbar = true
        this.snackbarColor = 'error'
    }
    showInfo(message: string, color = '') {
        this.snackbarText = message;
        this.snackbar = true
        this.snackbarColor = color
    }
    login() {
        this.showSubscriberOnlyDialog = false
        this.closeSidebarIfMobile()
        this.initialLoginOperation = 0
        this.dialog = 'login'
    }
    async logout() {
        try {
            await LogoutService.logout()
            await store.commit('logout')
            this.showInfo('Logged out successfully')
        } catch(error) {
            console.error(error)
            this.showError('Error logging out. Please clear cookies, as this will log you out.')
        }
    }
    locate() {
        this.locating = true
        navigator.geolocation.getCurrentPosition(result => {
            this.onLocated(result)
            this.locating = false
        },
        error => {
            console.error('position location error: ', error)
            this.locating = false
        })
    }
    onLocated(position: any) {
        const latLngCoord: Coordinate = [position.coords.longitude, position.coords.latitude]
        const coord = this.currentRenderer.TransformCoordFromLatLng(latLngCoord)
        this.currentRenderer.map.getView().setCenter(coord)
        const currentSize = this.currentRenderer.GetSize()
        this.currentRenderer.map.getView().adjustResolution(20000/currentSize)

        const point = new Point(coord)
        if(this.currentLocationMarker !== null) {
            this.currentLocationMarker.setGeometry(point)
        } else {
            this.currentLocationMarker = new Feature<Point>(point)
            const style = new Circle({
                radius: 5,
                displacement: [2,5]
            })

            this.currentLocationMarker.setStyle(Styles.CurrentLocationMarkerStyle)
            this.auxiliaryMarkersFeatureSource.addFeature(this.currentLocationMarker)
        }
    }
    onCloseDialog(message: string) {
        if (message) {
            this.showInfo(message)
        }
        if(this.dialog === 'login') {
            this.initialLoginWantsToUpgrade = false
        }
        this.dialog = ''
    }
    dismissPricingSidebar() {
        store.commit('showingPricingSidebar', false);
        if (store.getters.wantsToUpgrade) {
            LoginService.clearWantsToUpgrade().catch(e => console.error(e)); // doesn't matter to the user whether it succeeded or not
        }
    }
    async upgrade(price: Price, product: Product) {
        const priceId = price.id
        const mode = price.mode
        if (!store.getters.isUserLoggedIn) {
            store.commit('showingPricingSidebar', false);
            this.initialLoginOperation = 1 // register
            this.initialLoginWantsToUpgrade = true
            this.dialog = 'login'
            this.showInfo("Thanks! Please create an account first or login.")
        } else {
            try {
                this.priceIdUpgrading = priceId;

                // this is the 'publishable key'
                const stripeKey = "pk_live_51H8mf9H4u4N68KOCj4XAC5OYTGOYazN9aP1lnQMm8TWTbw9n11KY5hmqwpd5OCmNAja010R58SoTPW48Vfjg7psS00dBqmRpxA";
                const stripe = (window as any).Stripe(stripeKey)
                const offeredFreeTrial = this.offerFreeTrialForPrice(price, product)
                const stripeCheckoutSession = await StripeService.createStripeCheckoutSession(mode, priceId, offeredFreeTrial)
                stripe.redirectToCheckout({sessionId: stripeCheckoutSession.data.id}).then((result: any) => {
                    if(result.error) {
                        this.showError(result.error)
                    }
                })
            } catch(error) {
                this.priceIdUpgrading = null
                console.error(error)
                this.showError('An error occurred subscribing.')
            } finally {
                setTimeout(() => this.priceIdUpgrading = null, 5000) // give stripe a chance to launch the new window
            }
        }
    }
    undo() {
        // undo button is disabled if !canUndo, but the Ctrl+Z key is still enabled so need to check again here
        if (this.undoCommands.length > 0) { 
            const undoable = this.undoCommands.pop()
            if(undoable) {
                for(const command of undoable.commands) {
                    command.doUndoAction()
                }
            }

            this.closeSidebarIfMobile()
        }
    }
    showTraveller() {
        // "easter egg" : animate a traveller along the current track
        const track = this.getCurrentTrack()
        if (track) new Journey(track, this.waypoints).toggle()
    }
    persistLayers() {
        this.tracks.forEach((track) => track.saveFeaturesToLngLats(this.currentRenderer))
        this.waypoints.saveFeaturesToLngLats(this.currentRenderer)
    }
    clearLayers() {
        const layers = this.currentRenderer.map.getLayers()
        layers.forEach((layer: any) => {
            this.currentRenderer.map.removeLayer(layer) //after this, layer is undefined
        })
    }
    addTrackLayersToMap(track: TrackEntity) {
        this.currentRenderer.map.addLayer(track.createLayerForSegments())
        this.currentRenderer.map.addLayer(track.createLayerForDots())
    }
    waypointLayerZIndexAboveTracks = 3
    addWaypointLayerToMap() {
        this.waypointLayer = new WaypointLayer(this.waypoints, TrackVectorLayerType.Dots, {
            source: this.waypoints.dotsFeatureSource,
            zIndex: this.waypointLayerZIndexAboveTracks
        });
        this.currentRenderer.map.addLayer(this.waypointLayer)
    }
    addAuxiliaryMarkersLayerToMap() {
        this.auxiliaryMarkersLayer = new CustomVectorLayer({
            source: this.auxiliaryMarkersFeatureSource,
            zIndex: this.currentLocationZIndexAboveTracks
        })
        this.currentRenderer.map.addLayer(this.auxiliaryMarkersLayer)
    }
    elevationChartHoverIndicatorLayerZIndexAboveTracks = 4
    currentLocationZIndexAboveTracks = 5
    addElevationHoverIndicatorLayerToMap() {
        this.elevationChartHoverIndicatorLayer = new VectorLayer({
            source: this.elevationChartIndicatorFeatureSource,
            style: Styles.ElevationChartHoverIndicator,
            zIndex: this.elevationChartHoverIndicatorLayerZIndexAboveTracks
        })
        this.currentRenderer.map.addLayer(this.elevationChartHoverIndicatorLayer)
    }
    finishEditingWaypointName() {
        this.selectedWaypoint = null
    }
    rebuildLayers() {
        this.tracks.forEach((track) => {
            track.loadFeaturesFromLngLats(this.currentRenderer)
            this.addTrackLayersToMap(track)
        })
        this.currentRenderer.map.addLayer(new CustomVectorLayer({
            source: this.routingLineFeatureSource,
            style: Styles.ThickTransparentLightBlue
        }))
        this.waypoints.loadFeaturesFromLngLats(this.currentRenderer)
        this.addWaypointLayerToMap()
        this.addAuxiliaryMarkersLayerToMap()
        this.addElevationHoverIndicatorLayerToMap()
    }
    implementsCustomInteractionLifecycle(obj: any): obj is CustomInteractionLifecycle {
        return obj && obj.implementsCustomInteractionLifecycle === true
    }
    implementsCustomInteractionLifecycleTrack(obj: any): obj is CustomInteractionLifecycleTrack {
        return obj && obj.implementsCustomInteractionLifecycleTrack === true
    }
    setOperationForCurrentTrack() {
        const track = this.getCurrentTrack()

        const op = this.operations[ this.operation ];
        if (track) {
            store.commit('setCurrentTrackValue', track.value)

            this.currentOperations.forEach(op => {
                if (this.implementsCustomInteractionLifecycle(op)) {
                    op.finish()
                }    
            })
            this.clearInteractions();
            if (op && op.name === 'draw') {
                this.startOrContinueDrawingTrack(track)
            }
        }

        // don't necessarily need a current track for reroute
        if (op && op.name === 'drag') {
            this.setCorrectRerouteInteraction()
        }

        //... or split
        if(op && op.name === 'split') {
            const splitInteraction = new SplitTrack({
                renderer: this.currentRenderer
            })
            
            this.setCurrentInteractions([splitInteraction])
        }

        if (op && op.name === 'waypoint') {
            const waypointInteraction = new DrawWaypoint({
                renderer: this.currentRenderer,
                waypoint: this.waypoints
            })

            this.setCurrentInteractions([waypointInteraction])
        }

        if(op && op.name === 'move') {
            const selectTrack = new SelectTrack()
            const contextMenu = new ShowMenu({renderer: this.currentRenderer})
            this.setCurrentInteractions([selectTrack, contextMenu])
        }
    }
    setCorrectRerouteInteraction() {
        if(this.trackpointEditing) {
            this.setRerouteInteraction(RerouteIndividual)
        } else {
            this.setRerouteInteraction(Reroute)
        }
    }
    setRerouteInteraction<T extends RerouteBase>(rerouteType: RerouteConstructor<T>) {        
        if(!_.some(this.currentOperations, op => op instanceof rerouteType)) {
            this.clearInteractions()
            this.setCurrentInteractions([new rerouteType({
                routeDelegate: new RouteDelegateImp(this.currentRenderer),
                reroutingLineFeatureSource: this.routingLineFeatureSource,
                joinIndicatorFeatureSource: this.joinIndicatorFeatureSource,
                renderer: this.currentRenderer
            })])
        }
    }
    handleNewTrackEvent(event: Event|BaseEvent) {
        const newTrackEvent = event as NewTrackEvent
        newTrackEvent.newTrack.renderer = this.currentRenderer
        this.addTrackLayersToMap(newTrackEvent.newTrack)
        this.tracks.splice(0, 0, newTrackEvent.newTrack)
        // insert it at the front, so it's bottom z-index. seems more consistent this way - the new track is always below, 
        // rather than being sometimes above, sometimes below, depending on the recentness of selection of the former track
    }
    currentTrackChangedFromDropdown(val: string) {
        const track = this.tracks.find(t => t.value === val)
        if(track) {
            track.highlight()
            this.ensureTrackVisible(track)
        }
    }
    distanceUnitsChanged(newUnit: string) {
        this.currentRenderer.setScaleLineUnit(newUnit)
    }
    highlightSelectedWaypoint(newlySelectedWaypoint: WaypointFeature) {
        this.waypoints.dotsFeatureSource.forEachFeature(f => {
            if(f === newlySelectedWaypoint) {
                f.setStyle(WaypointIcon.iconFor(f as WaypointFeature, true))
            } else {
                f.setStyle(WaypointIcon.iconFor(f as WaypointFeature))
            }
        })
    }
    startOrContinueDrawingTrack(track: TrackEntity) {
        if (this.currentRenderer) {
            const routeDelegateImp = new RouteDelegateImp(this.currentRenderer)

            const routeInteraction = new DrawRoute({
                track: track, 
                routeDelegate: routeDelegateImp,
                routingLineFeatureSource: this.routingLineFeatureSource,
                joinIndicatorFeatureSource: this.joinIndicatorFeatureSource,
                renderer: this.currentRenderer
            })
            this.setCurrentInteractions([routeInteraction])
        }
    }
    restoreTrackSinceBeingEdited(data: RestoreTrackData) {
        // this restores a track back to how it was since being edited (rather than restoring it completely since having been deleted)
        //make sure we've still got the track
        const track = this.tracks.find(t => t.value === data.trackId)
        if(track) {
            track.restore(this.currentRenderer, data)
            this.currentOperations.forEach(op => {
                if(this.implementsCustomInteractionLifecycleTrack(op)) {
                    op.trackChanged()
                }    
            })
        }
    }
    reinstateDeletedTrack(data: ReinstateTrackData) {
        const reinstatedTrack = new TrackEntity(this.currentRenderer)
        reinstatedTrack.name = data.name
        reinstatedTrack.colour = data.colour
        reinstatedTrack.value = data.trackId
        reinstatedTrack.dotLngLats = data.dotLngLats
        reinstatedTrack.trackLngLatFeatures = data.trackLngLatFeatures
        this.addTrack(reinstatedTrack)
        const oldCurrentTrack = this.getCurrentTrack()
        this.currentTrack = reinstatedTrack.value
        if(oldCurrentTrack && oldCurrentTrack.value !== reinstatedTrack.value && oldCurrentTrack.isDegenerate()) {
            this.deleteSpecificTrack(new DeleteTrackData(oldCurrentTrack.value)) // we probably created a new one because the deleted one was the only one, so delete it 
        }
    }
    restoreWaypoint(data: RestoreWaypointData) {
        const coords = this.currentRenderer.TransformCoordFromLatLng(data.serialization.latLngCoords)
        const newIndex = this.waypoints.dotsFeatureSource.getFeatures().length
        const waypointFeature = new WaypointFeature(new Point(coords), this.waypoints, newIndex, data.serialization.props, data.serialization.id)
        this.waypoints.dotsFeatureSource.addFeature(waypointFeature)
    }
    deleteSpecificTrack(data: DeleteTrackData, addUndoCommand = true) {
        const trackIndex = this.tracks.findIndex(t => t.value === data.trackId)
        if(trackIndex > -1) {
            if(data.addUndoCommand !== null) {
                addUndoCommand = data.addUndoCommand
            }
            this.doDeleteTrackOfIndex(trackIndex, addUndoCommand)
        }
    }
    setCurrentTrack(data: string) {
        this.currentTrack = data
    }
    deleteSpecificWaypoint(data: DeleteWaypointData) {
        const waypointToRemove = this.waypoints.dotsFeatureSource.getFeatureById(data.waypointId)
        this.waypoints.dotsFeatureSource.removeFeature(waypointToRemove)
    }
    getCommandForUndoData(data: PreUndoData): UndoCommand {
        if(data instanceof RestoreTrackData) {
            return new UndoCommandParameterized<RestoreTrackData>(this.restoreTrackSinceBeingEdited.bind(this), data)
        } else if (data instanceof ReinstateTrackData) {
            return new UndoCommandParameterized<ReinstateTrackData>(this.reinstateDeletedTrack.bind(this), data)
        } else if(data instanceof RestoreWaypointData) {
            return new UndoCommandParameterized<RestoreWaypointData>(this.restoreWaypoint.bind(this), data)
        } else if(data instanceof DeleteTrackData) {
            return new UndoCommandParameterized<DeleteTrackData>(this.deleteSpecificTrack.bind(this), data)
        } else if(data instanceof DeleteWaypointData) {
            return new UndoCommandParameterized<DeleteWaypointData>(this.deleteSpecificWaypoint.bind(this), data)
        } else if (data instanceof SetCurrentTrackData) {
            const currentTrack = this.currentTrack
            return new UndoCommandParameterized<string>(this.setCurrentTrack.bind(this), currentTrack)
        } else if (data instanceof ReinstateWorkspaceData) {
            return new UndoCommandParameterized<ReinstateWorkspaceData>(this.reinstateWorkspace.bind(this), data)
        } else {
            throw new Error('PreUndoData not recognized ' + (typeof data))
        }
    }
    handlePreUndoEvent(preUndoEvent: PreUndoEvent) {
        this.undoCommands.push(new Undoable(preUndoEvent.data.map(this.getCommandForUndoData.bind(this)), preUndoEvent.name))
    }
    handleDeleteTrackEvent(deleteTrackEvent: DeleteTrackEvent) {
        this.deleteSpecificTrack(new DeleteTrackData(deleteTrackEvent.trackId), deleteTrackEvent.addUndoCommand)
    }
    handleInfoMessageEvent(infoMessageEvent: InfoMessageEvent) {
        this.showInfo(infoMessageEvent.message, infoMessageEvent.colour)
    }
    handleFinishedDrawingEvent(finishedDrawingEvent: Event|BaseEvent) {
        if(this.operations[this.operation].name === 'draw') {
            this.operation = 0
        }
    }
    setCurrentInteractions(interactions: Interaction[]) {
        this.currentOperations = interactions
        interactions.forEach(interaction => {
            interaction.addEventListener(CommonEvents.PreUndo, (event) => {
                this.handlePreUndoEvent(event as PreUndoEvent)
            })
            interaction.addEventListener(CommonEvents.DeleteTrack, (event) => {
                this.handleDeleteTrackEvent(event as DeleteTrackEvent)
            })
            interaction.addEventListener(CommonEvents.InfoMessage, (event) => {
                this.handleInfoMessageEvent(event as InfoMessageEvent)
                
            })
            interaction.addEventListener(DrawRouteEvents.finisheddrawing, (event) => {
                this.handleFinishedDrawingEvent(event)
            })
            interaction.addEventListener(SelectTrackEvent.EventType, this.handleTrackSelected.bind(this))
            interaction.addEventListener(ContextMenuEventType.RequestShow, this.handleRequestShowMenu.bind(this))
            interaction.addEventListener(ContextMenuEventType.RequestHide, this.handleRequestHideMenu.bind(this))
            interaction.addEventListener(SplitTrackEvents.newtrack, this.handleNewTrackEvent.bind(this))
            interaction.addEventListener(RerouteEvents.warning, (event: Event|BaseEvent) => {
                const warningEvent = event as RerouteWarningEvent
                this.showInfo(warningEvent.message)
            })
            interaction.addEventListener(RerouteEvents.routeerror, (event: Event|BaseEvent) => {
                const errorEvent = event as RerouteErrorEvent
                this.showError(errorEvent.message)
            })
            this.currentRenderer.map.addInteraction(interaction)
        })
    }
    clearInteractions() {
        this.currentOperations.forEach(op => {
            if (this.currentRenderer) {
                this.currentRenderer.map.removeInteraction(op)
                op.dispose()
            }
        })
        this.currentOperations = []
    }
    clearPermanentInteractions() {
        if(this.currentRenderer !== null) {
            for(const permanentInteraction of this.permanentInteractions) {
                this.currentRenderer.map.removeInteraction(permanentInteraction);
                permanentInteraction.dispose()
            }
            this.permanentInteractions = []
        }
    }
    isWaypointSelected(wpt: WaypointFeature) {
        return this.selectedWaypoint === wpt
    }
    addPermanentInteractions() {
        const editWaypointInteraction = new EditWaypoint({renderer: this.currentRenderer}, this, this)
        // a normal method call: this.handleWaypointSelected()
        editWaypointInteraction.addEventListener(WaypointEvents.WaypointSelected, this.handleWaypointSelected.bind(this))
        editWaypointInteraction.addEventListener(WaypointEvents.WaypointDeselected, this.handleWaypointDeselected.bind(this))
        this.addPermanentInteraction(editWaypointInteraction)

        const currentTrack = this.getCurrentTrack()
        if(this.elevationChart !== null && currentTrack) {
            const distanceUnits = this.getCurrentDistanceUnit()
            this.addPermanentInteraction(this.elevationChartHoverIndicator = new ElevationChartHoverIndicator(this.elevationChart, currentTrack, this.elevationChartIndicatorFeatureSource, distanceUnits))
        }
        this.addPermanentInteraction(new HoverDistanceInteraction(this.currentRenderer, this.appState))
    }
    getCurrentDistanceUnit() {
        return DistanceUnits.find(d => d.value === this.distanceUnit) || new DistanceUnit('Metres', 'metres', 'm', 1)
    }
    handleRequestShowMenu(ev: Event|BaseEvent) {
        const contextMenuEvent = ev as ContextMenuEvent
        this.currentRenderer.contextMenuControl.show(contextMenuEvent.pixel, contextMenuEvent.coordinate)
    }
    handleRequestHideMenu() {
        this.currentRenderer.contextMenuControl.hide()
    }
    setDefaultCursor(event: MapBrowserEvent<any>) {
        // only needs to be passed to permanent interactions, as others will only receive events when they are active - thus, they 'own the workspace'
        // and it's therefore up to them what they set their cursor to. Permanent interactions however have to integrate with others, so it's not necessarily up to them alone.
        let cursor;
        const op = this.operations[ this.operation ];
        if(!op) {
            throw new Error('Operation at index invalid: ' + this.operation)
        }
        if(op.name === 'move') {
            cursor = event.dragging ? 'grabbing ' : 'default'
        } else {
            cursor = 'crosshair'
        }
        this.currentRenderer.map.getTargetElement().style.cursor = cursor
    }
    handleTrackSelected(ev: Event|BaseEvent) {
        const trackSelectedEvent = ev as SelectTrackEvent
        this.currentTrack = trackSelectedEvent.track.value
        this.getCurrentTrack()?.highlight()
    }
    ensureTrackVisible(track: TrackEntity) {
        const mapExtent = this.currentRenderer.GetExtent()
        const trackExtent = track.GetExtent()
        if(!intersects(mapExtent, trackExtent)) {
            const newMapExtent = extend(mapExtent, trackExtent)
            this.currentRenderer.GoToExtent(newMapExtent)
        }
    }
    setTrackZIndexes() {
        const newTracks = this.tracks // avoid mutating property many times, for performance? can't do any harm
        const trackIndexToMoveToTop = newTracks.findIndex(p => p.value === this.currentTrack)
        let modified = false
        if(trackIndexToMoveToTop >= 0 && trackIndexToMoveToTop < newTracks.length - 1) { // if it's found, and not already at the top
            modified = true
            const trackToMoveToTop = newTracks.splice(trackIndexToMoveToTop, 1)[0]
            newTracks.push(trackToMoveToTop)
        }
        if(modified) {
            this.tracks = newTracks
        }
        let highestZIndex = 0
        this.currentRenderer.map.getLayers().forEach((l: any) => {
            if(l instanceof TrackVectorLayer) {
                const trackZIndex = newTracks.findIndex(t => t === l.track) + 1
                l.track.zIndex = trackZIndex
                const zIndex = l.track.zIndexForLayer(l.zIndexWithinTrack)
                l.setZIndex(zIndex)
                if(zIndex > highestZIndex) {
                    highestZIndex = zIndex
                }
            }
        })
        if(this.waypointLayer !== null) {
            this.waypointLayer.setZIndex(this.waypointLayerZIndexAboveTracks + highestZIndex + 1)
        }
        if(this.auxiliaryMarkersLayer !== null) {
            this.auxiliaryMarkersLayer.setZIndex(this.currentLocationZIndexAboveTracks + highestZIndex + 1)
        }
        if(this.elevationChartHoverIndicatorLayer !== null) {
            this.elevationChartHoverIndicatorLayerZIndexAboveTracks + highestZIndex + 1
            this.elevationChartHoverIndicatorLayer.setZIndex(this.elevationChartHoverIndicatorLayerZIndexAboveTracks + highestZIndex + 1)
        }
    }
    handleWaypointSelected(ev: Event|BaseEvent) {
        const waypointSelectedEvent = ev as WaypointSelectedEvent
        this.selectedWaypoint = waypointSelectedEvent.waypointSelected
        setTimeout(() => {
            const selectedWaypointNameTextField = document.getElementById('selectedWaypointNameTextField') as HTMLInputElement
            if(selectedWaypointNameTextField) {
                selectedWaypointNameTextField.select()
            }
        })
    }
    handleWaypointDeselected(ev: Event|BaseEvent) {
        this.selectedWaypoint = null
    }
    deleteWaypoint() {
        if(this.selectedWaypoint) {
            const latLngCoords = this.currentRenderer.TransformCoordToLatLng(this.selectedWaypoint.getGeometry().getCoordinates())
            const waypointSerialization = new WaypointSerialization(this.selectedWaypoint.value, this.selectedWaypoint.props, latLngCoords)
            this.undoCommands.push(new Undoable([this.getCommandForUndoData(new RestoreWaypointData(this.selectedWaypoint.value, waypointSerialization))], 'delete waypoint'))
            this.waypoints.dotsFeatureSource.removeFeature(this.selectedWaypoint)
            this.selectedWaypoint = null
        }
    }
    deleteAllUnnamedWaypoints() {
        const waypointsToDelete = this.waypoints.dotsFeatureSource.getFeatures().filter(f => f.props.name.startsWith(WPT_UNTITLED))
        if(waypointsToDelete.length) {
            const undoCommands = waypointsToDelete.map(wpt => {
                const latLngCoords = this.currentRenderer.TransformCoordToLatLng(wpt.getGeometry().getCoordinates())
                const waypointSerialization = new WaypointSerialization(wpt.value, wpt.props, latLngCoords)
                return this.getCommandForUndoData(new RestoreWaypointData(wpt.value, waypointSerialization))
            })
            this.undoCommands.push(new Undoable(undoCommands, 'delete all unnamed waypoints'))
            waypointsToDelete.forEach(w => this.waypoints.dotsFeatureSource.removeFeature(w))
        }
    }
    addPermanentInteraction(i: Interaction) {
        if(this.currentRenderer !== null) {
            this.currentRenderer.map.addInteraction(i)
            this.permanentInteractions.push(i)
        }
    }
    addNewTrack(options: AddNewTrackOptions|undefined = undefined) {
        const t = new TrackEntity(this.currentRenderer);
        this.tracks.push(t);
        this.currentTrack = t.value
        t.renderer = this.currentRenderer
        this.addTrackLayersToMap(t)
        if(!(options && options.dontSetDraw)) {
            this.$nextTick(() => {
                this.operation = 1
            })
        }
    }
    tryAddNewTrack() {
        if((store.getters.isSubscribed || this.tracks.length < 2) && !this.isFreeUserTryingToEditSharedTrack()) {
            this.addNewTrack()
        } else {
            this.showSubscribeOnlyDialog(freeUserTrackLimitMessage)
        }
    }
    renameTrack() {
        if(!this.isFreeUserTryingToEditSharedTrack()) {
            this.renamingTrack = true;
            setTimeout(() => {
                const rt1 = document.getElementById('renameTrackTextField') as HTMLInputElement
                if(rt1) {
                    rt1.select()
                }
            })
        }
    }
    async fileSelected(file: File|undefined) { // fileload
        if(file) {
            try {
                this.showLoadFileDialog = true
                const fileContents = await file.arrayBuffer()
                const gpxGeoJson = await GpxDownloadService.uploadGpx(fileContents, file.name)
                let overallExtent: Extent = createEmpty()
                const extentCallback: ((ext: Extent) => void) = ext => {
                    overallExtent = extend(overallExtent, ext)
                }
                this.addTracksFromGpxGeoJson(gpxGeoJson.data, extentCallback)
                this.addWaypointsFromGpxGeoJson(gpxGeoJson.data, extentCallback)
                scaleFromCenter(overallExtent, 1.1) // so features don't appear right on the edge
                this.currentRenderer.GoToLatLngExtent(overallExtent)
                this.closeSidebarIfMobile()
                this.operation = 0
            } catch(error) {
                ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) || this.showError('An error occurred processing the file.')
            } finally {
                this.showLoadFileDialog = false
                this.fileToUpload = null
            }
        }
    }
    cloudDelete(gpxInfo: CloudGpxInfo) { 
        this.cloudDeleteParams = _.cloneDeep(gpxInfo)
        this.dialog = 'confirmclouddelete'
    }
    showShareInfo(gpxInfo: CloudGpxInfo) {
        this.publicShareInfo = this.getPublicShareInfo(gpxInfo)
        this.showPublicShareDialog = true
    }
    getPublicShareInfo(gpxInfo: CloudGpxInfo, pointLimit?: number|undefined): PublicShareInfo {
        const retval: PublicShareInfo = {
            name: gpxInfo.name,
            url: `${process.env.VUE_APP_BASE_URL}/u/${gpxInfo.url}`,
            downloadUrl: `${GpxDownloadService.downloadGpxPublicUrl(gpxInfo.url, pointLimit, this.mergeSegmentsOnSaveToFile)}`,
        }
        return retval
    }
    async cloudDeleteDismiss(toDelete: boolean) {
        this.dialog = ''
        if(toDelete && this.cloudDeleteParams !== null) {
            const prefix = this.cloudDeleteParams.public ? "u" : "r"
            this.cloudDeleting = true
            this.progressMessage = "Deleting ... please wait"
            try {
                await CloudSaveService.CloudSaveDelete(prefix, encodeURIComponent(this.cloudDeleteParams.url))
                this.showInfo(`${this.cloudDeleteParams.name} successfully deleted.`)
                this.$store.commit(this.cloudDeleteParams.public ? 'deletePublicGpx' : 'deletePrivateGpx', this.cloudDeleteParams.url)
                if(this.$route.params && this.$route.params['prefix'] === prefix && this.$route.params['gpxUrl'] === this.cloudDeleteParams.url) { //deleting current - switch to home
                    this.$router.push('/')
                    this.loadedTrackOwnedByCurrentUser = null
                    this.loadedTrackGpxUrl = null
                    this.loadedTrackIsPublic = null            
                }
            }
            finally {
                this.cloudDeleting = false
                this.progressMessage = null
                this.dialog = ''
                this.cloudDeleteParams = null
            }
        } else {
            this.cloudDeleting = false
            this.dialog = ''
            this.cloudDeleteParams = null
        }
    }
    offerFreeTrialForPrice(price: Price, product: Product) {
        return price.allowsFreeTrial && !product.userHasAlreadyUsedUpFreeTrial
    }
    upgradeLabel(price: Price, product: Product) {
        return this.offerFreeTrialForPrice(price, product) ? 'Free trial' : 'Upgrade'
    }
    clearAll() {
        const undoData = new ReinstateWorkspaceData(this.getGpx(), this.loadedTrackGpxUrl, this.loadedTrackOwnedByCurrentUser, this.loadedTrackIsPublic, this.$router.currentRoute.path)
        this.undoCommands.push(new Undoable([this.getCommandForUndoData(undoData)], 'clear all')) //TODO: also sve the URL.
        TrackEntity.nextTrackId = 1
        while(this.doDeleteTrackOfIndex(this.tracks.length - 1, false)); //returns false when it's deleted the last one (and added default one)
        this.waypoints.dotsFeatureSource.clear()

        this.loadedTrackGpxUrl = null
        this.loadedTrackIsPublic = null
        this.loadedTrackOwnedByCurrentUser = null

        if(this.$router.currentRoute.path !== '/') {
            this.$router.push('/')
        }
        
    }
    reinstateWorkspace(data: ReinstateWorkspaceData) {
        const startedFromNoContent = this.noTrackContent()
        this.addTracksFromGpxGeoJson(data.gpx)
        this.addWaypointsFromGpxGeoJson(data.gpx)
        this.loadedTrackGpxUrl = data.loadedTrackGpxUrl
        this.loadedTrackIsPublic = data.loadedTrackIsPublic
        this.loadedTrackOwnedByCurrentUser = data.loadedTrackOwnedByCurrentUser
        if(this.$router.currentRoute.path !== data.routerPath) {
            this.$router.push(data.routerPath)
        }
        if(startedFromNoContent) {
            this.operation = 0
        }
    }
    gpxLink(gpxInfo: CloudGpxInfo) {
        const prefix = gpxInfo.public ? "u" : "r"
        return `/${prefix}/${gpxInfo.url}`
    }
    async showOrRedrawElevationChart() {
        const track = this.getCurrentTrack()
        if(track) {
            this.gettingElevationData = this.elevationChart === null // don't show the loading banner if the chart already exists, as it looks better
            try
            { 
                const feature = track.asLatLongGeoJsonFeatureCollection()
                if(feature !== null) {
                    const distanceUnits = this.getCurrentDistanceUnit()
                    const chartAreaWidthInPx = (this.elevationChart) ? this.elevationChart.chartArea.width : this.currentRenderer.map.getViewport().clientWidth - 82 // approx chart area width in pixels, use map area if chart not yet visible
                    const elevationChartData = await ElevationChartHelper.getElevationChartData(feature, this.currentRenderer.Extent(), chartAreaWidthInPx)
                    const chartConfig = this.configureElevationChart(elevationChartData, distanceUnits)
                    let recreating = false
                    if(this.elevationChart !== null) {
                        this.elevationChart.destroy() // update doesn't work, I've tried!
                        this.elevationChart = null
                        recreating = true
                    }
                    if(this.elevationChartHoverIndicator) {
                        this.currentRenderer.map.removeInteraction(this.elevationChartHoverIndicator)
                        this.elevationChartHoverIndicator.dispose()
                    }
                    const canvas = document.getElementById('elevationchart') as HTMLCanvasElement
                    const chartElement = canvas.getContext('2d')!;
                    this.elevationChartNeedsToRepaintBackground = true
                    this.elevationChart = new Chart(chartElement, chartConfig)
                    this.addPermanentInteraction(this.elevationChartHoverIndicator = new ElevationChartHoverIndicator(this.elevationChart, track, this.elevationChartIndicatorFeatureSource, distanceUnits))
                    if(recreating) {
                        setTimeout(() => this.elevationChart?.update(), 0) // doesn't repaint the background properly otherwise. if we are showing for the first time, the resize events will be (more than) enough to force repaint
                    }
                }
            } catch(error) {
                console.error(error)
                this.showError('Error getting elevation data. Please report this to admin@gpxplanner.app if it keeps happening.')
            } finally {
                this.gettingElevationData = false
            }
        }
    }
    get elevationChartTitle() {
        return (this.tracks.length > 1 || !(/^Untitled\d+$/.test(this.currentTrackName))) ? this.currentTrackName+": "+this.elevationChartClimbingSuffix : this.elevationChartClimbingSuffix 
    }

    elevationChartBeforeRender(chart: Chart, args: { cancelable: true }, options: AnyObject, elevationData: ElevDataPoint[]) {
        if(!this.elevationChartNeedsToRepaintBackground) {
            return
        }

        const backgroundFill = chart.ctx.createLinearGradient(0, 0, chart.width, 0)
        const borderFill = chart.ctx.createLinearGradient(0, 0, chart.width, 0)

        const lastPoint = _.last(elevationData)
        const firstPoint =  _.first(elevationData)

        const blendAmount = 0.2
            // the proportion into each segment to add the colour stop.
            // Larger values make it more blended. 0 for no shading at all. Maximum = 0.5.

        if (firstPoint && lastPoint) {
            const range = lastPoint.x - firstPoint.x
            const proportionAcrossArea = (point: ElevDataPoint) => (point.x - firstPoint.x) / range
            const proportionAcrossCanvas = (point: ElevDataPoint) => (chart.chartArea.left + (proportionAcrossArea(point) * chart.chartArea.width)) / chart.width

            if(range > 0) {  
                for(let i = 1; i < elevationData.length; i++) {
                    const point0 = elevationData[i-1]
                    const point1 = elevationData[i]
                    // for each *pair* of points, the gradient *between* which is that of the rightmost (point1)
                    const colour = Styles.getColourForGradient(point1.gradient)

                    if(isNaN(point0.x) || isNaN(point1.x)) continue; // NaN = break in the elev chart track lies off the visible map
                    
                    const pc1 = proportionAcrossCanvas(point0)
                    const pc2 = proportionAcrossCanvas(point1)
                    const blendWidth = (pc2 - pc1) * blendAmount
                    const colourStart = i == 1 ? pc1 : pc1 + blendWidth // for the last and first one, add the stop right at the start or end.
                    const colourEnd = i == elevationData.length - 1 ? pc2 : pc2 - blendWidth
                    backgroundFill.addColorStop(colourStart, colour.backgroundColor)
                    backgroundFill.addColorStop(colourEnd, colour.backgroundColor)
                }
            }
        }

        const dataset = chart.data.datasets[0]
        dataset.backgroundColor = backgroundFill
        dataset.borderColor = borderFill
        this.elevationChartNeedsToRepaintBackground = false
        return true
    }

    configureElevationChart(elevationChartDataInfo: ElevationChartDataInfo, distanceUnit: DistanceUnit): ChartConfiguration {
        // Elevation is always plotted in metres against a Metric y-axis; if we are showing elevation
        // in feet, we just hide the Metric axis and show an Imperial (feet) y-axis instead. You don't 
        // have to set the maximum value on the y-axis, in fact chart.js does an arguably nicer job of 
        // tickmarks, grid and scale without it, but we need the scale for both the axes to match, so we
        // need set it.

        // y-axis range in metres

        const elevationChartData = elevationChartDataInfo.dataSetStandard
        const showMetricAxis = distanceUnit.metricElevation
        const minyM = Math.floor( _.minBy(elevationChartData, p => p.y)?.y! )
        const absMaxyM = _.maxBy(elevationChartData, p => p.y)?.y! 
        const maxyM = Math.floor( minyM + (absMaxyM-minyM) * 1.05 + 0.5) // put 5% headroom on the y-axis so that the max value does not collide with the top of the chart area
        
        // y-axis range in feet
        const showImperialAxis = !showMetricAxis
        const minyFt = Math.floor(minyM * feetPerMetre + 0.5) 
        const maxyFt = Math.floor(maxyM * feetPerMetre + 0.5)

        // x-axis range in metres
        const minxM = _.first(elevationChartData)?.x ?? 0 
        const maxxM = _.last(elevationChartData)?.x ?? 0 

        // x-axis range in distance units
        const mPerDu = this.getCurrentDistanceUnit().metres
        const minxDU = minxM / mPerDu
        const maxxDU = maxxM / mPerDu

        // Find (approximate) total ascent in M and Ft
        let ascent = 0;
        for (let i=0, j=1; j<elevationChartData.length; i++, j++) {
            const p0 = elevationChartData[i].y
            const p1 = elevationChartData[j].y
            if (p1 > p0) ascent += p1 - p0
        }
        const ascentM  = Math.floor (ascent + 0.5)
        const ascentFt = Math.floor (ascent * feetPerMetre + 0.5)

        // Show total ascent in title
        this.elevationChartClimbingSuffix = (showMetricAxis ? ascentM.toLocaleString()+"m" : ascentFt.toLocaleString()+"ft") + " total ascent"
        const title = this.elevationChartTitle

        const defaultColour = Styles.getColourForGradient(0)

        const datasets: ChartDataset<keyof ChartTypeRegistry, ScatterDataPoint[]>[] = [
            {
                data: elevationChartData,
                fill: true,
                showLine: true,
                order: 2,
                tension: 0.2, // FeatureToggles.getToggle(Toggle.Tension)
                radius: 0,
                normalized: true,
                backgroundColor: defaultColour.backgroundColor,
                borderColor: defaultColour.borderColor
            }, 
/*            {
                data: elevationChartDataInfo.dataSetSimplifiedForGradientCalc,
                fill: true,
                showLine: true,
                order: 1,
                tension: 0.2, // FeatureToggles.getToggle(Toggle.Tension)
                radius: 0,
                normalized: true,
                backgroundColor: 'gray',
                borderColor: 'black'
            }, 
*/            
            {
                data: [], // for the ElevationChartHoverIndicator
                backgroundColor: Styles.elevChartHoverIndicatorBg, 
                radius: 10,
                hoverRadius: 10,
                pointBorderWidth: 2,
                pointBorderColor: Styles.elevChartHoverIndicatorFg, 
                normalized: true
            }
        ]
        return {
            type: 'line',
            data: {
                datasets: datasets
            },
            plugins: [{
                id: 'gradientshading',
                beforeDraw: (chart, args, options) => this.elevationChartBeforeRender(chart, args, options, elevationChartDataInfo.dataSetSimplifiedForGradientCalc)
            }],
            options: {
                onHover: _.debounce((evt: ChartEvent, elements: ActiveElement[], chart: Chart) => {
                    // ElevChart-to-Map visualisation...
                    
                    // seems to keep firing even when the mouse isn't moving... so check the event has changed
                    if(evt.native && this.elevationChartLastHoverEventHasChanged(evt)) {
                        const nearestPointOnLine = HomeMethods.elevationChartInterpolateEventToLine(evt, chart)
                        const track = this.getCurrentTrack()
                        if(nearestPointOnLine && track) {
                            // mouse is close to a the chart line, so display the indicator(s)
                            const distanceFromEvent = DistanceHelper.distance({x: nearestPointOnLine[0], y: nearestPointOnLine[1]}, evt)
                            const indicatorDataset = ElevationChartHelper.getIndicatorDataset(chart)
                            if(distanceFromEvent !== null && distanceFromEvent < 20) {
                                const proportionOfChart = (nearestPointOnLine[0] - chart.chartArea.left) / chart.chartArea.width
                                const proportionOfTrack = (minxM + proportionOfChart * (maxxM - minxM)) / track.totalDistance
                                const coord = track.getCoordAtProportion(proportionOfTrack)
                                if(coord) {
                                    while(indicatorDataset.data.length > 0) {
                                        indicatorDataset.data.pop()
                                    }
                                    const dataPoint = HomeMethods.elevationChartCoordToDataPoint(chart, nearestPointOnLine)
                                    indicatorDataset.data.push(dataPoint)
                                    this.elevationChartIndicatorFeatureSource.getOrAddFeature().setGeometry(new Point(coord))
                                    chart.update('none')
                                }
                            } else if (distanceFromEvent === null || distanceFromEvent > 50) {
                                // mouse is far from the chart line, so clear the indicator(s)
                                let needToUpdate = false
                                while(indicatorDataset.data.length > 0) {
                                    indicatorDataset.data.pop()
                                    needToUpdate = true
                                }
                                if(needToUpdate) {
                                    chart.update('none')
                                }
                                this.elevationChartIndicatorFeatureSource.clear()
                            }
                        }
                    }
                }),
                maintainAspectRatio: false,
                scales: {
                    x: {
                        type: 'linear',
                        min: minxM,
                        max: maxxM,
                        display: false
                    },
                    y: {
                        display: showMetricAxis,
                        title: {
                            align: 'center',
                            display: true,
                            text: 'Altitude (metres)',
                        },
                        min: minyM,
                        max: maxyM 
                    },
                    xDisplay: { // chart-js treats it as an x-axis by convention as its name starts with x
                        type: 'linear',
                        min: minxDU,
                        max: maxxDU,
                        title: {
                            align: 'center',
                            display: true,
                            text: 'Distance' + this.distanceUnitSuffix
                        }
                    },
                    yImperial: {
                        display: showImperialAxis,
                        title: {
                            align: 'center',
                            display: true,
                            text: 'Altitude (feet)',
                        },
                        min: minyFt,
                        max: maxyFt
                    }
                },
                plugins: {
                    title: {
                        display: true,
                        text: title
                    }
                }
            }
        }
    }
    get distanceUnitSuffix() {
        const du = DistanceUnits.find(du => du.value === this.distanceUnit)
        if(du) {
            return ' (' + du.distanceDescription + ')'
        }
        return ''
    }
    static elevationChartCoordToDataPoint(chart: Chart, point: Coordinate): ScatterDataPoint {
        //0,0 is top left
        const xProportion = (point[0] - chart.chartArea.left) / chart.chartArea.width
        const yProportion = 1 - (point[1] - chart.chartArea.top) / chart.chartArea.height
        const xaxis = chart.scales['x']
        const yaxis = chart.scales['y']
        return { x: xaxis.min + (xaxis.max - xaxis.min) * xProportion, y: yaxis.min + (yaxis.max - yaxis.min) * yProportion }
    }

    static elevationChartInterpolateEventToLine(evt: ChartEvent, chart: Chart): Coordinate|null {
        if(evt.x !== null && evt.y !== null) {
            const ds1Meta = chart.getDatasetMeta(0)
            if (ds1Meta.data.length > 0) {
                let pointToLeft = findNearestDataPoint(ds1Meta.data, evt.x, p => p.x)
                if(pointToLeft !== -1) {
                    if(pointToLeft >= ds1Meta.data.length - 1) {
                        pointToLeft = pointToLeft - 1 // if we're off the right, we might as well still just use the right most line, as the nearest point will be the same
                    }
                    const p1 = ds1Meta.data[pointToLeft]
                    const p2 = ds1Meta.data[pointToLeft + 1]
                    const chartSegment = new LineString([[p1.x, p1.y], [p2.x, p2.y]]) // they're not geographic lines, but LineString's algorithms still work (see tests)
                    const nearestPointToEvent = chartSegment.getClosestPoint([evt.x, evt.y])
                    return nearestPointToEvent
                }
            }
        }
        return null
    }
    elevationChartLastHoverEventHasChanged(evt: ChartEvent): boolean {
        let retval = false
        retval = this.elevationChartLastHoverEvent === null || this.elevationChartLastHoverEvent.x !== evt.x || this.elevationChartLastHoverEvent.y !== evt.y
        this.elevationChartLastHoverEvent = evt
        return retval
    }
    static distanceFromEvent(evt: ChartEvent, item: InteractionItem): number|null {
        if(evt.x !== null && evt.y !== null) {
            return Math.sqrt(Math.pow(evt.x - item.element.x, 2) + Math.pow(evt.y - item.element.y, 2))
        } else {
            return null
        }
    }
    closeElevation(event: Event) {
        this.elevationChart?.destroy()
        this.elevationChart = null
        if(this.elevationChartHoverIndicator !== null) {
            this.currentRenderer.map.removeInteraction(this.elevationChartHoverIndicator)
            this.elevationChartHoverIndicator.dispose()
        }
        this.elevationChartIndicatorFeatureSource.clear()
    }
    finishRenamingTrack(event: Event) {
        this.renamingTrack = false
        const elevationChartTitlePlugin = this.elevationChart?.options?.plugins?.title
        if(elevationChartTitlePlugin) {
            elevationChartTitlePlugin.text = this.elevationChartTitle
            this.elevationChart?.update()
        }
    }
    paneRemoved(event: Event) {
        setTimeout(() => {
            this.currentRenderer.map.updateSize()
        }, 500 ) // 0 doesn't seem to work well enough
    }
    paneResized(panes: PaneSpec[]) {
        if(panes[1]) { // still fires when the elevation chart pane has closed
            const elevationChartPaneSize = panes[1].size
            StorageHelper.setItem('elevationChartPaneSize', elevationChartPaneSize.toString())
            setTimeout(() => {
                this.currentRenderer.map.updateSize()
            }, 500 ) // 0 doesn't seem to work well enough
        }
    }
    toggletrackpointEditing(event: Event) {
        this.trackpointEditing = !this.trackpointEditing
    }
    get showtrackpointEditing() {
        const op = this.operations[this.operation]
        return op && op.name === 'drag'
    }
    updateShowingTrackpoints() {
        //should be largely idempotent
        const layers = this.currentRenderer.map.getLayers().getArray()
        const op = this.operations[this.operation]
        const isEitherReroutingMode = op && op.name === 'drag'
        const trackpointEditing = this.trackpointEditing && isEitherReroutingMode

        const currentTrack = this.getCurrentTrack()
        this.tracks.forEach(t => t.showingTrackpoints = trackpointEditing && t.value === this.currentTrack)

        if(trackpointEditing && currentTrack && !_.some(layers, l => l instanceof TrackVectorLayer && l.type === TrackVectorLayerType.Trackpoints && l.track.value === currentTrack.value)) {
            //layer for current track not already in map, add it
            const layer = currentTrack.createLayerForTrackpoints()
            this.currentRenderer.map.addLayer(layer)
        }
        // remove any trackpoint layer apart from the one for the current track if we're editing, and even the current track if we're not point editing.
        layers.forEach((l: any) => {
            if(l instanceof TrackVectorLayer && l.type === TrackVectorLayerType.Trackpoints && (l.track.value !== this.currentTrack || !trackpointEditing)) {
                this.currentRenderer.map.removeLayer(l)
            }
        })
        
        if(isEitherReroutingMode) {
            this.setCorrectRerouteInteraction()
        }
    }
    async handleRestoreCachedGpxCommand(command: RestoreCachedGpxCommand) {
        try {
            const response = await LoginService.CachedGpx(command.savedGpx)
            if(response && response.data) {
                this.addTracksFromGpxGeoJson(response.data)
                this.addWaypointsFromGpxGeoJson(response.data)
                this.operation = 0
            }
        } catch(error) {
            console.error(error) // don't rethrow - not absolutely vital
        }
    }
    async handleRestoreCachedGpxCommandAlreadyRetrievedCommand(command: RestoreCachedGpxAlreadyRetrievedCommand) {
        try {
            this.addTracksFromGpxGeoJson(command.gpx)
            this.addWaypointsFromGpxGeoJson(command.gpx)
        } catch(error) {
            console.error(error) // don't rethrow - not absolutely vital
        }
    }

    // async handleGoogleLoginCommand(command: GoogleLoginCommand) {
    //     try {
    //         console.log(`Logging in with Google, the code is ${command.code}`);
    //         const loginresp = await LoginService.loginGoogle(command.code, command.state);
    //         if (loginresp.data !== null) {
    //             store.commit('login', loginresp.data);   
    //         }
    //     } catch(error) {
    //         ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) || this.showError('An error occurred logging in with Google.');
    //     }
    // }

    rejoinToNextStage() {
        const joinHelper = new JoinHelper(this.joinIndicatorFeatureSource, this, this.currentRenderer)
        try {
            const track = this.getCurrentTrack();
            if(track) {
                const tracksToJoinTo: any[] = [];
                this.tracks.forEach(trackToJoinTo => {
                    if(track !== trackToJoinTo) { 
                        const endOfThis = track.getCoordinate(-1, -1)
                        const startOfOther = trackToJoinTo.getCoordinate(0, 0)
                        if(startOfOther !== null && endOfThis !== null) {
                            const dist = distance(endOfThis, startOfOther)
                            if(dist < 500) {
                                tracksToJoinTo.push({
                                    distance: dist,
                                    track: trackToJoinTo
                                })
                            }
                        }
                    }
                })

                if(tracksToJoinTo.length > 0) {
                    const mostLikelyNextTrackByName = this.getMostLikelyNextTrackByName(track, tracksToJoinTo.map(a => a.track))
                    if(mostLikelyNextTrackByName) {
                        joinHelper.joinTracks(track, mostLikelyNextTrackByName.track, true)
                        return true // restart the loop, as the tracks will have changed
                    } else {
                        const mostLikelyByDistance = _.first(tracksToJoinTo.sort(a => a.distance))
                        if(mostLikelyByDistance) {
                            joinHelper.joinTracks(track, mostLikelyByDistance.track, true)
                            return true // restart the loop, as the tracks will have changed
                        }
                    }
                } else {
                    this.showInfo(`There seems to be no other track within 500m of the end of '${track.name}'.`)
                }
            }
        } catch(error) {
            console.error(error)
        }
    }

    getMostLikelyNextTrackByName(thisTrack: TrackEntity, otherTracks: TrackEntity[]) {
        const names = [thisTrack.name].concat(otherTracks.map(t => t.name))
        const lengthOfCommonPrefix = this.getLengthOfCommonPrefix(names)
        if(lengthOfCommonPrefix === 0) {
            return null
        }
        const numberOfThisTrack = parseInt(thisTrack.name.substring(lengthOfCommonPrefix))
        if(isNaN(numberOfThisTrack)) {
            return null
        }
        const bestGuessesAtOtherTrackInOrder = otherTracks.map(t => {
            return {track: t, number: parseInt(t.name.substring(lengthOfCommonPrefix))};
        })
            .filter(a => !isNaN(a.number) && a.number > numberOfThisTrack)
            .sort(a => a.number)
        return _.first(bestGuessesAtOtherTrackInOrder) ?? null
    }

    getLengthOfCommonPrefix(names: string[]) {
        const minLength = _.min(names.map(n => n.length)) ?? 0
        for(let i = minLength; i >= 1; i--) {
            const distinctValues = _.uniqBy(names, n => n.substring(0, i))
            if(distinctValues.length === 1) {
                return i
            }
        }
        return 0
    }

    rejoinSplitTracks() {
        const joinHelper = new JoinHelper(this.joinIndicatorFeatureSource, this, this.currentRenderer)
        for(const element of this.tracks) {
            const track = element
            const trackToJoinTo = this.tracks.find(trackToJoinTo =>   {
                if(track === trackToJoinTo) { 
                    return false
                }
                const endOfThis = track.getCoordinate(-1, -1)
                const startOfOther = trackToJoinTo.getCoordinate(0, 0)
                return startOfOther !== null && endOfThis !== null && equals(endOfThis, startOfOther)
            })

            if(trackToJoinTo) {
                joinHelper.joinTracks(track, trackToJoinTo)
            }
        }
    }

    get downloadOptions() {
        const downloadOptions = [
            {value: AllTracksInOneGpx, text: `All tracks in one GPX`}
        ]
        if(this.tracks.length > 1) {
            const currentTrack = this.getCurrentTrack()
            if(currentTrack) {
                downloadOptions.push({value: CurrentTrack, text: `Just current track: ${currentTrack.name}`})
            }

            downloadOptions.push({value: 'GpxPerTrackInZipFile', text: 'ZIP file with one GPX file per track'})
        }
        return downloadOptions
    }



    getCloudGpxs(cloudGpxType: CloudGpxType) {
        try
        {
            const gpxsUnsorted: CloudGpxInfo[] = store.getters.loggedInUser[`${cloudGpxType.name.toLowerCase()}Gpxs`]
            const trimmedFilter = (this.cloudGpxFilter || '').trim().toLowerCase()
            const gpxsFiltered = gpxsUnsorted.filter(g => !!g.name && g.name.toLowerCase().indexOf(trimmedFilter) >= 0)
            switch(this.cloudGpxSortDirections[this.cloudGpxSortDirection]) {
                case NameAZ: return _.orderBy(gpxsFiltered, s => s.name)
                case NameZA: return _.orderBy(gpxsFiltered, s => s.name, "desc")
                case OldNew: return _.orderBy(gpxsFiltered, s => s.modified)
                case NewOld: return _.orderBy(gpxsFiltered, s => s.modified, "desc")
                default: return gpxsUnsorted
            }
        } catch(error) {
            console.error(error)
            throw error
        }
    }

    cloudGpxFilterClearClicked() {
        this.cloudGpxFilter = '' // you would think this would happen automatically, but seemingly not
    }
}